import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import { CSSObject, styled, Theme, useTheme } from '@mui/material/styles';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import SearchIcon from '@mui/icons-material/Search';
import SettingsIcon from '@mui/icons-material/Settings';
import TopBarRoutes from 'common/data/TopBarRoutes';
import { Link } from 'react-router-dom';
import { AllowConfigurationModule } from 'common/data/Permissions/ConfigurationAccess';
import logo from './../assets/img/full-logo-dark.png';
import WizCircularLogo from './WizCircularLogo';
import UnifiedSearchDialog from './Dialogs/UnifiedSearchDialog';
import {
  GetMyUser,
  GetSettingBySettingName,
  UpdateSettingBySettingName,
} from '../services/UsersService';
import { ExtendedUser } from '../common/models/Configuration/User';
import SideNavBarModuleRoot from './Navigation/NavBar/SideNavBarModuleRoot';
import NavBarHelpMenu from './Navigation/NavBarHelpMenu';
import NavBarUserMenu from './Navigation/NavBarUserMenu';
import CustomApplicationContext from 'util/CustomApplicationContext';
import NavBarNotifications from './Navigation/NavBarNotifications';
import { NavBarRecentRecordsMenu } from './Navigation/NavBarRecentRecordsMenu';
import { NavBarFavoritesMenu } from './Navigation/NavBarFavoritesMenu';
import ServerInfoDialog from './Dialogs/Help/ServerInfoDialog';
import { useKindeAuth } from '@kinde-oss/kinde-auth-react';
import { NameIdObjString } from 'common/models/GenericTypes';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import CreateOpportunityHandlerDialog from './Dialogs/Opportunities/CreateOpportunityHandlerDialog';

const drawerWidth = 240;
const triggerCloseEventName = 'VerticalNavCloseEvent';
const sideBarStateSettingName = 'VerticalNavState';

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

export const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

const CustomDrawer = styled(Drawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    variants: [
      {
        props: ({ open }) => open,
        style: {
          ...openedMixin(theme),
          '& .MuiDrawer-paper': openedMixin(theme),
        },
      },
      {
        props: ({ open }) => !open,
        style: {
          ...closedMixin(theme),
          '& .MuiDrawer-paper': closedMixin(theme),
        },
      },
    ],
  })
);

type SideBarRoutes = {
  text: string;
  icon: React.ReactNode;
  allow?: () => boolean;
} & (
  | {
      type: 'action';
      action: () => void;
    }
  | {
      type: 'link';
      href: string;
    }
);

export default function NavBar() {
  const theme = useTheme();
  const [open, setOpen] = useState(true);
  const [showUnifiedSearchDialog, setShowUnifiedSearchDialog] = useState(false);
  const [showServerInfoDialog, setShowServerInfoDialog] = useState(false);
  const [currentUser, setCurrentUser] = useState<ExtendedUser | null>(null);
  const [selectedModule, setSelectedModule] = useState<number | null>(null);
  const [showCreateOpportunityDialog, setShowCreateOpportunityDialog] = useState(false);
  const { getClaim, getOrganization, login } = useKindeAuth();

  const kindeOrgs = useMemo<NameIdObjString[]>(() => {
    const orgs = getClaim('organizations', 'id_token');
    if (orgs?.value) return orgs.value as NameIdObjString[];
    return [];
  }, [getClaim]);

  const currentOrgId = useMemo(() => {
    if (getOrganization) {
      return getOrganization()?.orgCode ?? '';
    }
    return '';
  }, [getOrganization]);

  useEffect(() => {
    const getData = async () => {
      const res = await GetMyUser();
      if (res) setCurrentUser(res);
    };
    getData();
  }, []);

  useEffect(() => {
    const getData = async () => {
      const res = await GetSettingBySettingName(sideBarStateSettingName);
      if (res) setOpen(res === 'true');
    };
    getData();
  }, []);

  useEffect(() => {
    const delayResizeDispatch = setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 170);
    return () => clearTimeout(delayResizeDispatch);
  }, [open]);

  const toggleSideBarHandler = useCallback(async () => {
    setOpen((prev) => {
      const val = !prev;
      UpdateSettingBySettingName(sideBarStateSettingName, val.toString());
      return val;
    });
  }, []);

  const localHotkeyHandler = useCallback((e: KeyboardEvent) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
      e.preventDefault();
      setShowUnifiedSearchDialog((prev) => !prev);
    }

    if ((e.ctrlKey || e.metaKey) && e.key === 'i') {
      e.preventDefault();
      setShowServerInfoDialog((prev) => !prev);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', localHotkeyHandler);
    return () => document.removeEventListener('keydown', localHotkeyHandler);
  }, [localHotkeyHandler]);

  const triggerCloseHandler = useCallback((e: Event) => {
    if (e.type === triggerCloseEventName) setOpen(false);
  }, []);

  useEffect(() => {
    document.addEventListener(triggerCloseEventName, triggerCloseHandler);
    return () => document.removeEventListener(triggerCloseEventName, triggerCloseHandler);
  }, [triggerCloseHandler]);

  const openCloseHotkeyHandler = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === ']') {
        e.preventDefault();
        toggleSideBarHandler();
      }
    },
    [toggleSideBarHandler]
  );

  useEffect(() => {
    document.addEventListener('keydown', openCloseHotkeyHandler);
    return () => document.removeEventListener('keydown', openCloseHotkeyHandler);
  }, [openCloseHotkeyHandler]);

  const textColor = useMemo(() => {
    return theme.palette.getContrastText(theme.palette.primary.main);
  }, [theme.palette]);

  const bottomRoutes = useMemo<SideBarRoutes[]>(() => {
    return [
      {
        text: 'Configuration',
        type: 'link',
        href: '/configuration',
        icon: <SettingsIcon sx={{ color: textColor }} />,
        allow: AllowConfigurationModule,
      },
    ];
  }, [textColor]);

  const orgChangeHandler = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const orgId = e.target.value;
      if (orgId) {
        localStorage.clear();
        login({ org_code: orgId });
      }
    },
    [login]
  );

  return (
    <>
      <CreateOpportunityHandlerDialog
        open={showCreateOpportunityDialog}
        closeHandler={() => setShowCreateOpportunityDialog(false)}
      />
      <UnifiedSearchDialog
        open={showUnifiedSearchDialog}
        closeHandler={() => setShowUnifiedSearchDialog(false)}
      />
      <ServerInfoDialog
        open={showServerInfoDialog}
        closeHandler={() => setShowServerInfoDialog(false)}
      />
      <CustomDrawer
        variant="permanent"
        open={open}
        PaperProps={{
          sx: {
            bgcolor: '#000872',
            color: textColor,
            borderRight: 'none',
          },
        }}
      >
        <DrawerHeader>
          {open ? (
            <>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                }}
              >
                <Link to="/home" style={{ marginTop: '5px', marginRight: '10px', lineHeight: 0 }}>
                  <img src={logo} alt="Recruit Wizard Logo" width={200} />
                </Link>
                {Boolean(currentOrgId) && kindeOrgs.length > 1 && (
                  <TextField
                    select
                    value={currentOrgId}
                    onChange={orgChangeHandler}
                    variant="standard"
                    slotProps={{
                      input: { disableUnderline: true, sx: { color: 'inherit' } },
                    }}
                    sx={{ '.MuiSvgIcon-root': { fill: 'white' } }}
                  >
                    {kindeOrgs.map((o) => (
                      <MenuItem key={o.id} value={o.id}>
                        {o.name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
                {(!currentOrgId || kindeOrgs.length <= 1) && (
                  <Typography variant="subtitle2" sx={{ mr: '10px' }}>
                    {currentUser?.customerName ?? ''}
                  </Typography>
                )}
              </Box>

              <IconButton
                onClick={toggleSideBarHandler}
                sx={{
                  background: 'white',
                  zIndex: 1000,
                  position: 'fixed',
                  top: '59px',
                  transform: open ? 'translateX(18px)' : 'translateX(0)', // Use transform for smooth transition
                  border: '0.1px solid #000872',
                  width: '14px',
                  height: '14px',
                  transition: 'transform 0.3s ease-in-out', // Smooth transition for transform
                  '&:hover': {
                    background: 'white',
                    border: '0.1px solid #000872',
                  },
                }}
              >
                <ChevronLeftIcon sx={{ color: '#000872', width: '14px', height: '14px' }} />
              </IconButton>
            </>
          ) : (
            <>
              <Link to="/home">
                <IconButton>
                  <WizCircularLogo />
                </IconButton>
              </Link>
              <IconButton
                onClick={toggleSideBarHandler}
                sx={{
                  background: 'white',
                  zIndex: 1000,
                  position: 'fixed',
                  top: '56px',
                  transform: open ? 'translateX(0px)' : 'translateX(18px)', // Use transform for smooth transition
                  border: '0.1px solid #000872',
                  width: '14px',
                  height: '14px',
                  transition: 'transform 0.3s ease-in-out', // Smooth transition for transform
                  '&:hover': {
                    background: 'white',
                    border: '0.1px solid #000872',
                  },
                }}
              >
                <ChevronRightIcon sx={{ color: '#000872', width: '14px', height: '14px' }} />
              </IconButton>
            </>
          )}
        </DrawerHeader>
        <Divider variant="middle" sx={{ backgroundColor: 'white' }} />
        <List sx={{ overflowY: 'auto', overflowX: 'clip', direction: 'rtl' }}>
          <ListItem disablePadding sx={{ display: 'block', direction: 'ltr' }}>
            <Tooltip title="Search" placement="right" disableHoverListener={open}>
              <ListItemButton
                sx={{ paddingY: 0, px: 2.5, justifyContent: open ? 'initial' : 'center' }}
                onClick={() => setShowUnifiedSearchDialog(true)}
              >
                <ListItemIcon sx={{ minWidth: 0, justifyContent: 'center', mr: open ? 3 : 'auto' }}>
                  <SearchIcon sx={{ color: textColor }} />
                </ListItemIcon>
                <ListItemText primary="Search" sx={{ opacity: open ? 1 : 0 }} />
              </ListItemButton>
            </Tooltip>
          </ListItem>
          <NavBarFavoritesMenu isSideBarOpen={open} />
          <NavBarRecentRecordsMenu isSideBarOpen={open} />
          {TopBarRoutes.map((item, index) => (
            <SideNavBarModuleRoot
              key={index}
              module={item}
              moduleIndex={index}
              selectedModule={selectedModule}
              isSideBarOpen={open}
              changeSelectedModule={setSelectedModule}
              showCreateOpportunityDialogHandler={() => setShowCreateOpportunityDialog(true)}
            />
          ))}
        </List>
        <List sx={{ mt: 'auto' }}>
          <Divider variant="middle" sx={{ backgroundColor: 'white' }} />
          <CustomApplicationContext.Consumer>
            {(c) => (
              <NavBarUserMenu
                currentUser={c.currentUser}
                currentUserRefreshHandler={c.refreshCurrentUser}
                isSideBarOpen={open}
              />
            )}
          </CustomApplicationContext.Consumer>
          {bottomRoutes.map((item, index) => (
            <ListItem key={index} disablePadding sx={{ display: 'block', marginTop: '6px' }}>
              {item.type === 'link' && (
                <Link to={item.href} style={{ textDecoration: 'none', color: 'inherit' }}>
                  <Tooltip title={item.text} placement="right" disableHoverListener={open}>
                    <ListItemButton
                      sx={{ paddingY: 0, px: 2.5, justifyContent: open ? 'initial' : 'center' }}
                    >
                      <ListItemIcon
                        sx={{ minWidth: 0, justifyContent: 'center', mr: open ? 3 : 'auto' }}
                      >
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText primary={item.text} sx={{ opacity: open ? 1 : 0 }} />
                    </ListItemButton>
                  </Tooltip>
                </Link>
              )}
            </ListItem>
          ))}
          <NavBarNotifications isSideBarOpen={open} />
          <NavBarHelpMenu isSideBarOpen={open} />
        </List>
      </CustomDrawer>
    </>
  );
}
